import React, { useState } from "react";
import { motion } from "framer-motion";
import { Layers, Cog, Sun, Waves, Flower2 } from "lucide-react";
import { LOGO, EngineeringTruss } from "../Brand";

const META = [
  { key: "foundations", icon: EngineeringTruss, title: "Engineering Foundations",
    short: "Legacy · Discipline",
    body: "The track-inspired geometry represents the disciplined engineering mindset on which DEBSUN stands. It symbolises precision, reliability, structured thinking and the continuous pursuit of progress." },
  { key: "steel", icon: Layers, title: "Steel Industry",
    short: "Strength · Foundation",
    body: "Steel is the industrial foundation of our work — the strength and durability that underpins every panel, structure and system we deliver." },
  { key: "automation", icon: Cog, title: "Automation",
    short: "Precision · Progress",
    body: "The gear represents precision engineering and smart industrial processes — the automation that turns raw capability into repeatable, reliable performance." },
  { key: "corona", icon: Sun, title: "Sun & Corona",
    short: "Energy · Intelligence",
    body: "The golden corona is energy, light and the core of innovation that drives change — the spark at the centre of everything DEBSUN builds." },
  { key: "aurora", icon: Waves, title: "Aurora Borealis",
    short: "Inspiration · Possibility",
    body: "The aurora wave stands for innovation, intelligence and limitless possibilities — the restless, layered creativity that moves us forward." },
  { key: "bengal", icon: Flower2, title: "Bengali Heritage",
    short: "Culture · Soul",
    body: "The motif carries culture, soul and the values that keep us grounded — a reminder that engineering, at its best, serves people and place." },
];

export default function Meaning() {
  const [active, setActive] = useState("corona");

  return (
    <section id="meaning" className="relative py-20 sm:py-28 lg:py-36 overflow-hidden scroll-mt-[88px]" data-testid="meaning">
      <img src={LOGO} alt="" className="watermark w-[46vw] max-w-[560px] left-[-8%] bottom-[-6%]" />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-6">
            <div className="sec-index mb-4">01 — Identity</div>
            <h2 className="display display-section text-4xl md:text-6xl">The meaning of <span style={{ color: "var(--gold)" }}>DEBSUN</span>.</h2>
            <div className="font-mono-2 text-[11px] tracking-[0.35em] uppercase text-[color:var(--gold)] mt-4">Our Foundation. Our Direction.</div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base md:text-lg text-steel-text leading-relaxed">
              DEBSUN is founded on the values of Debashish and Sunita Bhattacharya —
              a family foundation of discipline, integrity and long-term thinking.
              Built by Abhilash Bhattacharya, the company carries those values into
              engineering practice today: precise work, honest capability, and a clear
              vision for the industrial systems DEBSUN continues to build.
            </p>
          </div>
        </div>

        <div className="gold-rule gold-rule-node mb-14" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Icon grid */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border">
            {META.map((m, i) => {
              const Icon = m.icon;
              const on = active === m.key;
              return (
                <motion.button
                  key={m.key}
                  onMouseEnter={() => setActive(m.key)}
                  onFocus={() => setActive(m.key)}
                  onClick={() => setActive(m.key)}
                  data-testid={`meaning-${m.key}`}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className={`relative text-left p-7 aspect-square flex flex-col justify-between transition-colors duration-500 ${
                    on ? "bg-secondary" : "bg-background hover:bg-secondary/50"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <Icon size={28} strokeWidth={1.4}
                      className="transition-colors duration-500"
                      style={{ color: on ? "var(--gold)" : "var(--steel)" }} />
                    <span className="font-mono-2 text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <div>
                    <div className="display display-section text-lg leading-tight">{m.title}</div>
                    <motion.div
                      initial={false} animate={{ width: on ? "40px" : "0px" }}
                      transition={{ duration: 0.4 }}
                      className="h-px mt-3" style={{ background: "linear-gradient(90deg,var(--teal),var(--purple))" }} />
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-5">
            {META.filter((m) => m.key === active).map((m) => {
              const Icon = m.icon;
              return (
                <motion.div
                  key={m.key}
                  initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                  className="steel-card clip-frame h-full p-9 flex flex-col justify-between"
                  data-testid="meaning-detail"
                >
                  <div>
                    <Icon size={44} strokeWidth={1.2} style={{ color: "var(--gold)" }} />
                    <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mt-8">{m.short}</div>
                    <h3 className="display display-section text-3xl md:text-4xl mt-3">{m.title}</h3>
                    <p className="mt-6 text-base text-steel-text leading-relaxed">{m.body}</p>
                  </div>
                  <div className="mt-10 flex items-center gap-3">
                    {META.map((x) => (
                      <span key={x.key} className="h-1 flex-1 transition-colors duration-500"
                        style={{ background: x.key === active ? "var(--gold)" : "hsl(var(--hairline))" }} />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
